import type { FC, ReactNode } from 'react'
import type { ArticleDetail } from '@/lib/content/types'

import { cmsKey } from '@/lib/portal/cms'
import { formatDate } from '@/lib/format'
import { CoverArt } from '@/components/site/cover-art'
import { AuthorAvatar, Breadcrumb, contentSection, contentTable, field, summaryOf, TopicChip } from '@/components/site/article-meta'
import { useLocale } from '@/i18n/use-locale'
import { common } from '@/i18n/common'
import { siteContent } from '@/i18n/site-content'

interface ArticleHeaderProps {
    item: ArticleDetail
}

export const ArticleHeader: FC<ArticleHeaderProps> = ({ item }): ReactNode => {
    const locale = useLocale()
    const copy = siteContent[locale]
    const shared = common[locale]
    const table = contentTable(item.type)
    const title = field(item, 'title', locale)
    const summary = summaryOf(item, locale)
    const kind = item.kind ? field(item, 'kind', locale) : null
    const author = item.author
    const role = author ? field(author, 'role', locale) : null
    const sectionLabel = item.type === 'insight' ? shared.nav.insights : shared.nav.blog

    return (
        <>
            <header className='relative overflow-hidden bg-mist pb-28 pt-32 sm:pb-36 sm:pt-36'>
                <div className='bg-grid mask-fade-b pointer-events-none absolute inset-0 opacity-60' aria-hidden='true' />
                <div className='pointer-events-none absolute -right-40 -top-40 size-[520px] rounded-full bg-primary/15 blur-3xl' aria-hidden='true' />

                <div className='container-page relative'>
                    <Breadcrumb
                        label={copy.article.breadcrumb}
                        items={[
                            { href: '/', label: shared.breadcrumbHome },
                            { href: contentSection(item.type), label: sectionLabel },
                            { label: <span data-mdlfy-cms={cmsKey(table, item.id, title.column)}>{title.value}</span> }
                        ]}
                    />

                    <div className='mt-10 flex flex-wrap items-center gap-x-3 gap-y-2'>
                        <TopicChip item={item} locale={locale} />

                        {kind && (
                            <span className='text-xs font-semibold uppercase tracking-[0.18em] text-primary' data-mdlfy-cms={cmsKey(table, item.id, kind.column)}>
                                {kind.value}
                            </span>
                        )}
                    </div>

                    <h1 className='mt-6 max-w-4xl font-display text-[2.6rem] leading-[1.04] tracking-[-0.015em] text-foreground sm:text-6xl lg:text-[4.1rem]'>
                        <span data-mdlfy-cms={cmsKey(table, item.id, title.column)}>
                            {title.value}
                        </span>
                    </h1>

                    {summary.value && (
                        <p className='mt-6 max-w-3xl text-lg leading-relaxed text-muted-foreground sm:text-xl' data-mdlfy-cms={cmsKey(table, item.id, summary.column)}>
                            {summary.value}
                        </p>
                    )}

                    <div className='mt-10 flex max-w-4xl flex-wrap items-center gap-x-10 gap-y-5 border-t border-border pt-6'>
                        {author && (
                            <div className='flex items-center gap-3'>
                                <AuthorAvatar author={author} />

                                <div className='text-sm leading-snug'>
                                    <p className='font-semibold text-foreground'>
                                        {`${shared.by} `}
                                        <span data-mdlfy-cms={cmsKey('authors', author.id, 'name')}>{author.name}</span>
                                    </p>

                                    {role?.value && (
                                        <p className='mt-0.5 text-muted-foreground' data-mdlfy-cms={cmsKey('authors', author.id, role.column)}>
                                            {role.value}
                                        </p>
                                    )}
                                </div>
                            </div>
                        )}

                        <dl className='flex flex-wrap gap-x-10 gap-y-3 text-sm'>
                            {item.published_at && (
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground'>
                                        {copy.article.published}
                                    </dt>

                                    <dd className='mt-1 text-foreground'>
                                        <time dateTime={item.published_at} suppressHydrationWarning>
                                            {formatDate(item.published_at, locale, 'long')}
                                        </time>
                                    </dd>
                                </div>
                            )}

                            {item.read_minutes && (
                                <div>
                                    <dt className='text-xs font-semibold uppercase tracking-[0.16em] text-muted-foreground'>
                                        {copy.article.readingTime}
                                    </dt>

                                    <dd className='mt-1 text-foreground'>
                                        <span data-mdlfy-cms={cmsKey(table, item.id, 'read_minutes')}>{item.read_minutes}</span>
                                        {` ${shared.minRead}`}
                                    </dd>
                                </div>
                            )}
                        </dl>
                    </div>
                </div>
            </header>

            <div className='container-page relative -mt-16 sm:-mt-24'>
                <figure className='relative aspect-[16/9] overflow-hidden rounded-3xl border border-border bg-mist shadow-[0_40px_80px_-50px_rgba(13,21,38,0.5)]'>
                    {item.cover ? (
                        // eslint-disable-next-line @next/next/no-img-element
                        <img
                            src={item.cover}
                            alt={item.cover_alt ?? ''}
                            className='absolute inset-0 size-full object-cover'
                            decoding='async'
                        />
                    ) : (
                        <CoverArt seed={item.slug} label={copy.types[item.type]} className='absolute inset-0 size-full' />
                    )}
                </figure>
            </div>
        </>
    )
}
